import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import AuthClient from "../api/AuthClient";
import NewSeenGroupsApi from "../api/NewSeenTabApi";
import { parseMyPartyResponse } from "../api/parseResponse/parseMyPartiesResponse";
import { PartyModel } from "../types";
import { getSeenGroupIds, markGroupAsSeen, SeenGroupEntry } from "../utils/seenGroupsDb";

export type PromoterGroupsRequestState = "idle" | "loading" | "success" | "error";

interface PromoterGroupsContextData {
  groups: PartyModel[];
  requestState: PromoterGroupsRequestState;
  errorMessage: string;
  fetchGroups: () => Promise<void>;
  hasFetched: boolean;
  seenGroupIds: SeenGroupEntry[];
  markSeen: (id: string, partyDate: Date) => void;
}

export const PromoterGroupsContext = createContext<PromoterGroupsContextData>({
  groups: [],
  requestState: "idle",
  errorMessage: "",
  fetchGroups: async () => {},
  hasFetched: false,
  seenGroupIds: [],
  markSeen: () => {},
});

export const PromoterGroupsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [groups, setGroups] = useState<PartyModel[]>([]);
  const [requestState, setRequestState] = useState<PromoterGroupsRequestState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasFetched, setHasFetched] = useState(false);
  const [seenGroupIds, setSeenGroupIds] = useState<SeenGroupEntry[]>([]);

  useEffect(() => {
    getSeenGroupIds()
      .then(setSeenGroupIds)
      .catch((err) => console.error("Error loading seen group ids:", err));
  }, []);

  const fetchGroups = useCallback(async () => {
    setRequestState("loading");
    try {
      const api = new NewSeenGroupsApi(new AuthClient());
      const response = await api.getMyNewParties();

      if (response?.data && response.data.length > 0) {
        setGroups(response.data.map(parseMyPartyResponse));
        setErrorMessage("");
      } else if (response?.data === undefined && response?.errorMessage) {
        setErrorMessage(response.errorMessage);
        setGroups([]);
      } else {
        setGroups([]);
        setErrorMessage("");
      }
      setHasFetched(true);
      setRequestState("success");
    } catch (err) {
      console.error("Error fetching promoter groups:", err);
      setRequestState("error");
    }
  }, []);

  const markSeen = useCallback((id: string, partyDate: Date) => {
    setSeenGroupIds((prev) =>
      prev.some((g) => g.id === id) ? prev : [...prev, { id, partyDate }]
    );
    markGroupAsSeen(id, partyDate).catch((err) =>
      console.error("Error persisting seen group:", err)
    );
  }, []);

  const value = useMemo(
    () => ({
      groups,
      requestState,
      errorMessage,
      fetchGroups,
      hasFetched,
      seenGroupIds,
      markSeen,
    }),
    [groups, requestState, errorMessage, fetchGroups, hasFetched, seenGroupIds, markSeen]
  );

  return (
    <PromoterGroupsContext.Provider value={value}>
      {children}
    </PromoterGroupsContext.Provider>
  );
};
